import LoadError from "../components/LoadError";
import { useConfirm } from "../components/ConfirmProvider";
import { Plus, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { clientsAPI, parametresAPI, tachesAPI } from "../services/api";
import { useSearchParams } from "react-router-dom";

import { EmptyTaskState, TaskStatusSection } from "../components/tasks/TaskRows";
import { TaskFormModal } from "../components/tasks/TaskCreateModal";
import TaskDetailsDrawer from "../components/tasks/TaskDetailsDrawer";
import { TASK_STATUSES } from "../components/tasks/taskConstants";
import { roundHours } from "../components/tasks/taskUtils";
import toast from "react-hot-toast";

const Taches = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const confirm = useConfirm();
  const [tasks, setTasks] = useState([]);
  const fetchSequence = useRef(0);
  const fetchController = useRef(null);
  const [bootstrapError, setBootstrapError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [defaultRate, setDefaultRate] = useState(75);
  const [filters, setFilters] = useState({
    client_id: searchParams.get("client_id") || "",
    statut: "",
    facturable: "all",
    facturee: false,
    q: "",
  });
  const [onlyUnbilledTime, setOnlyUnbilledTime] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({
    a_faire: false,
    en_cours: false,
    bloque: false,
    a_facturer: false,
    terminee: false,
  });
  const [pendingKeys, setPendingKeys] = useState({});
  const [quickCreate, setQuickCreate] = useState(null);
  const [quickCreating, setQuickCreating] = useState(false);

  const loadBootstrap = async () => {
    setBootstrapError("");
    try {
      const [clientsResponse, paramsResponse] = await Promise.all([clientsAPI.getAll(), parametresAPI.get()]);
      setClients(clientsResponse.data || []); setDefaultRate(Number(paramsResponse.data?.taux_horaire_defaut || 75));
    } catch { setBootstrapError("Impossible de charger les clients et le tarif par défaut. Réessayez avant de créer une tâche."); }
  };
  useEffect(() => { loadBootstrap(); }, []);

  useEffect(() => {
    const timer = setTimeout(loadTasks, filters.q ? 250 : 0);
    return () => { clearTimeout(timer); fetchSequence.current += 1; fetchController.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.client_id,
    filters.statut,
    filters.facturable,
    filters.facturee,
    filters.q,
  ]);

  useEffect(() => {
    if (selectedTaskId && !tasks.some((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(null);
    }
  }, [selectedTaskId, tasks]);

  const clientMap = useMemo(
    () =>
      clients.reduce((acc, client) => {
        acc[client.id] = client;
        return acc;
      }, {}),
    [clients]
  );

  const visibleStatuses = useMemo(() => {
    if (filters.statut) return [filters.statut];
    return TASK_STATUSES.map((status) => status.value);
  }, [filters.statut]);

  const groupedTasks = useMemo(
    () =>
      visibleStatuses.reduce((acc, status) => {
        acc[status] = tasks.filter((task) => task.statut === status && (!onlyUnbilledTime || (task.facturable && Number(task.heures_a_facturer) > 0)));
        return acc;
      }, {}),
    [tasks, visibleStatuses, onlyUnbilledTime]
  );

  const selectedTask =
    tasks.find((task) => task.id === selectedTaskId) || null;

  const setPending = (key, nextValue) => {
    setPendingKeys((current) => {
      if (!nextValue) {
        const updated = { ...current };
        delete updated[key];
        return updated;
      }
      return { ...current, [key]: true };
    });
  };

  const replaceTask = (nextTask) => {
    setTasks((current) =>
      current.map((task) => (task.id === nextTask.id ? nextTask : task))
    );
  };

  const removeTaskLocal = (taskId) => {
    setTasks((current) => current.filter((task) => task.id !== taskId));
  };

  const loadTasks = async () => {
    const sequence = ++fetchSequence.current;
    fetchController.current?.abort();
    const controller = new AbortController(); fetchController.current = controller;
    setLoading(true); setLoadError("");
    try {
      const params = {};
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.statut) params.statut = filters.statut;
      if (filters.facturable !== "all") {
        params.facturable = filters.facturable === "true";
      }
      if (filters.facturee) params.facturee = false;
      if (filters.q.trim()) params.q = filters.q.trim();

      const response = await tachesAPI.getAll(params, controller.signal);
      if (sequence !== fetchSequence.current) return;
      setTasks(response.data || []);
    } catch (error) {
      if (sequence === fetchSequence.current && !controller.signal.aborted) setLoadError("Impossible de charger les tâches. Réessayez.");
    } finally {
      if (sequence === fetchSequence.current) setLoading(false);
    }
  };

  const syncClientFilter = (clientId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (clientId) {
      nextParams.set("client_id", clientId);
    } else {
      nextParams.delete("client_id");
    }
    setSearchParams(nextParams);
  };

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    if (field === "client_id") {
      syncClientFilter(value);
    }
  };

  const handleCreateTask = async (payload) => {
    try {
      const response = await tachesAPI.create(payload);
      setTasks((current) => [response.data, ...current]);
      setShowCreateModal(false);
      toast.success("Tâche créée");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la création");
      throw error;
    }
  };

  const handlePatchTask = async (task, patch, pendingKey) => {
    if (!task || Object.keys(patch).length === 0) return task;

    const previousTask = tasks.find((item) => item.id === task.id);
    if (!previousTask) return task;

    const optimisticTask = {
      ...previousTask,
      ...patch,
      updated_at: new Date().toISOString(),
    };

    setPending(pendingKey, true);
    replaceTask(optimisticTask);

    try {
      const response = await tachesAPI.patch(task.id, patch);
      replaceTask(response.data);
      return response.data;
    } catch (error) {
      replaceTask(previousTask);
      toast.error(error?.response?.data?.detail || "Erreur lors de la mise à jour");
      throw error;
    } finally {
      setPending(pendingKey, false);
    }
  };

  const handleAddTimeEntry = async (task, payload, pendingKey) => {
    const previousTask = tasks.find((item) => item.id === task.id);
    if (!previousTask) return task;

    const delta = roundHours(payload.delta_heures);
    const nextTotal = roundHours(Number(previousTask.heures_passees || 0) + delta);

    const optimisticTask = {
      ...previousTask,
      heures_passees: nextTotal,
      heures_a_facturer: roundHours(
        Math.max(0, nextTotal - Number(previousTask.heures_facturees || 0))
      ),
      updated_at: new Date().toISOString(),
      time_entries: [
        ...(previousTask.time_entries || []),
        {
          id: `temp-${Date.now()}`,
          delta_heures: delta,
          note: payload.note,
          created_at: new Date().toISOString(),
        },
      ],
    };

    setPending(pendingKey, true);
    replaceTask(optimisticTask);

    try {
      const response = await tachesAPI.addTimeEntry(task.id, payload);
      replaceTask(response.data);
      return response.data;
    } catch (error) {
      replaceTask(previousTask);
      toast.error(
        error?.response?.data?.detail || "Erreur lors de la mise à jour du temps"
      );
      throw error;
    } finally {
      setPending(pendingKey, false);
    }
  };

  const handleUpdateTimeEntry = async (task, entry, payload, pendingKey) => {
    const previousTask = tasks.find((item) => item.id === task.id);
    if (!previousTask) return task;

    const previousDelta = roundHours(entry.delta_heures);
    const nextDelta = roundHours(payload.delta_heures);
    const nextTotal = roundHours(
      Number(previousTask.heures_passees || 0) - previousDelta + nextDelta
    );
    const nextNote = String(payload.note || "");
    const optimisticTask = {
      ...previousTask,
      heures_passees: nextTotal,
      heures_a_facturer: roundHours(
        Math.max(0, nextTotal - Number(previousTask.heures_facturees || 0))
      ),
      updated_at: new Date().toISOString(),
      time_entries: (previousTask.time_entries || []).map((item) =>
        String(item.id) === String(entry.id)
          ? { ...item, delta_heures: nextDelta, note: nextNote }
          : item
      ),
    };

    setPending(pendingKey, true);
    replaceTask(optimisticTask);

    try {
      const response = await tachesAPI.updateTimeEntry(task.id, entry.id, {
        delta_heures: nextDelta,
        note: nextNote,
      });
      replaceTask(response.data);
      return response.data;
    } catch (error) {
      replaceTask(previousTask);
      toast.error(
        error?.response?.data?.detail ||
          "Erreur lors de la mise à jour de l'entrée de temps"
      );
      throw error;
    } finally {
      setPending(pendingKey, false);
    }
  };

  const handleDeleteTimeEntry = async (task, entry, pendingKey) => {
    const previousTask = tasks.find((item) => item.id === task.id);
    if (!previousTask) return task;

    const nextTotal = roundHours(
      Number(previousTask.heures_passees || 0) -
        roundHours(entry.delta_heures)
    );
    const optimisticTask = {
      ...previousTask,
      heures_passees: nextTotal,
      heures_a_facturer: roundHours(
        Math.max(0, nextTotal - Number(previousTask.heures_facturees || 0))
      ),
      updated_at: new Date().toISOString(),
      time_entries: (previousTask.time_entries || []).filter(
        (item) => String(item.id) !== String(entry.id)
      ),
    };

    setPending(pendingKey, true);
    replaceTask(optimisticTask);

    try {
      const response = await tachesAPI.deleteTimeEntry(task.id, entry.id);
      replaceTask(response.data);
      toast.success("Entrée de temps supprimée");
      return response.data;
    } catch (error) {
      replaceTask(previousTask);
      toast.error(
        error?.response?.data?.detail ||
          "Erreur lors de la suppression de l'entrée de temps"
      );
      throw error;
    } finally {
      setPending(pendingKey, false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) return;

    const ok = await confirm({ title: "Supprimer cette tâche ?", description: "La tâche et ses entrées de temps seront supprimées définitivement.", confirmLabel: "Supprimer la tâche", danger: true, details: [["Tâche", task.titre], ["Temps suivi", `${task.heures_passees || 0} h`]] });
    if (!ok) return;

    const pendingKey = `drawer:${taskId}:delete`;
    setPending(pendingKey, true);
    removeTaskLocal(taskId);
    setSelectedTaskId(null);

    try {
      await tachesAPI.delete(taskId);
      toast.success("Tâche supprimée");
    } catch (error) {
      setTasks((current) => [task, ...current].sort((a, b) =>
        String(b.updated_at || "").localeCompare(String(a.updated_at || ""))
      ));
      toast.error(error?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setPending(pendingKey, false);
    }
  };

  const handleQuickCreateActivate = (status) => {
    const activeClients = clients.filter((client) => !client.archived);
    if (bootstrapError || !activeClients.length) {
      toast.error("Ajoutez d'abord un client avant de créer une tâche");
      return;
    }
    setQuickCreate({
      status,
      titre: "",
      client_id: activeClients.find((client) => client.id === filters.client_id)?.id || activeClients[0]?.id || "",
    });
  };

  const handleQuickCreateSubmit = async () => {
    if (!quickCreate) return;
    const titre = String(quickCreate.titre || "").trim();
    if (!titre) {
      toast.error("Le titre est obligatoire");
      return;
    }
    if (!quickCreate.client_id) {
      toast.error("Le client est obligatoire");
      return;
    }

    setQuickCreating(true);
    try {
      const response = await tachesAPI.create({
        client_id: quickCreate.client_id,
        titre,
        statut: quickCreate.status,
        commentaire: "",
        notes_internes: "",
        heures_estimees: null,
        taux_horaire: clients.find((client) => client.id === quickCreate.client_id)?.taux_horaire ?? defaultRate,
        date_tache: new Date().toISOString().slice(0, 10),
        facturable: true,
      });
      setTasks((current) => [response.data, ...current]);
      setQuickCreate(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setQuickCreating(false);
    }
  };

  const toggleGroup = (status) => {
    setCollapsedGroups((current) => ({
      ...current,
      [status]: !current[status],
    }));
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tâches</h1>
          <p className="text-slate-600">
            Vue de pilotage quotidienne avec édition rapide en ligne.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="btn-primary w-full sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvelle tâche</span>
        </button>
      </div>

      <div className="card space-y-4 border-slate-200 bg-white/95">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <label htmlFor="task-search" className="mb-1 block text-sm font-medium text-slate-700">
              Recherche
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                className="input-field pl-9"
                id="task-search"
                placeholder="Titre ou commentaire"
                value={filters.q}
                onChange={(event) => handleFilterChange("q", event.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="taches-14749" className="mb-1 block text-sm font-medium text-slate-700">
              Client
            </label>
            <select id="taches-14749"
              className="input-field"
              value={filters.client_id}
              onChange={(event) =>
                handleFilterChange("client_id", event.target.value)
              }
            >
              <option value="">Tous les clients</option>
              {clients.filter((client) => !client.archived || client.id === filters.client_id).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="taches-15388" className="mb-1 block text-sm font-medium text-slate-700">
              Statut
            </label>
            <select id="taches-15388"
              className="input-field"
              value={filters.statut}
              onChange={(event) => handleFilterChange("statut", event.target.value)}
            >
              <option value="">Tous</option>
              {TASK_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="taches-15992" className="mb-1 block text-sm font-medium text-slate-700">
              Facturable
            </label>
            <select id="taches-15992"
              className="input-field"
              value={filters.facturable}
              onChange={(event) =>
                handleFilterChange("facturable", event.target.value)
              }
            >
              <option value="all">Toutes</option>
              <option value="true">Facturables</option>
              <option value="false">Non facturables</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.facturee}
            onChange={(event) =>
              handleFilterChange("facturee", event.target.checked)
            }
          />
          Masquer les tâches déjà facturées au moins une fois
        </label>
      </div>

      <LoadError message={bootstrapError} onRetry={loadBootstrap} />
      <LoadError message={loadError} onRetry={loadTasks} />
      {loading && <p role="status" className="text-sm text-gray-500">Actualisation des tâches…</p>}
      {tasks.length === 0 && !loadError && !loading ? <EmptyTaskState /> : null}

      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyUnbilledTime} onChange={(e) => setOnlyUnbilledTime(e.target.checked)} />Afficher uniquement les tâches avec du temps restant à facturer</label>
      {visibleStatuses.map((status) => (
          <TaskStatusSection
            key={status}
            status={status}
            tasks={groupedTasks[status] || []}
            collapsed={collapsedGroups[status]}
            clientMap={clientMap}
            showClient={!filters.client_id}
            activeCell={activeCell}
            setActiveCell={setActiveCell}
            pendingKeys={pendingKeys}
            onToggle={toggleGroup}
            onOpenDetails={setSelectedTaskId}
            onPatchTask={handlePatchTask}
            onAddTimeEntry={handleAddTimeEntry}
            quickCreate={quickCreate}
            clients={clients.filter((client) => !client.archived)}
            filteredClientId={filters.client_id}
            quickCreating={quickCreating && quickCreate?.status === status}
            onActivateQuickCreate={handleQuickCreateActivate}
            onCancelQuickCreate={() => setQuickCreate(null)}
            onQuickCreateChange={(field, value) =>
              setQuickCreate((current) => ({ ...current, [field]: value }))
            }
            onQuickCreateSubmit={handleQuickCreateSubmit}
          />
        ))}
      </div>

      {showCreateModal && (
        <TaskFormModal
          clients={clients.filter((client) => !client.archived)}
          defaultRate={defaultRate}
          onCancel={() => setShowCreateModal(false)}
          onSubmit={handleCreateTask}
        />
      )}

      <TaskDetailsDrawer
        task={selectedTask}
        clients={clients}
        open={Boolean(selectedTask)}
        saving={Boolean(selectedTask && pendingKeys[`drawer:${selectedTask.id}`])}
        pendingKeys={pendingKeys}
        deleting={Boolean(
          selectedTask && pendingKeys[`drawer:${selectedTask.id}:delete`]
        )}
        onClose={() => setSelectedTaskId(null)}
        onSave={handlePatchTask}
        onUpdateTimeEntry={handleUpdateTimeEntry}
        onDeleteTimeEntry={handleDeleteTimeEntry}
        onDelete={handleDeleteTask}
      />
    </div>
  );
};

export default Taches;
